import { useModal } from '@appsumo/dorado-react';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, Transition } from '@headlessui/react';
import { useContext, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';

import DiscussionDeleteModal from './DiscussionDeleteModal';
import { PDPContext } from '~/lib/product/context';
import { Deal } from '~/types/deal';
import { useRouter } from 'next/router';
import { CommentV2Type } from '~/static/discussions/enums';
import { isIndexablePage } from '~/lib/util/constants';
import { useReviewStats } from '~/hooks/userReviewStats';

function DiscussionOptions({
  deleteUrl,
  discusstionType,
  mutateComment = () => {},
}: Readonly<{
  deleteUrl: string;
  discusstionType:
    | typeof CommentV2Type.Review
    | typeof CommentV2Type.Question
    | typeof CommentV2Type.Reply;
  mutateComment?: () => void;
}>) {
  const [showOptions, setShowOptions] = useState(false);
  const { deal }: { deal: Deal } = useContext(PDPContext);
  const { mutateReviewStats } = useReviewStats(deal.id);
  const { asPath, push } = useRouter();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const [showModal, hideModal] = useModal(() => (
    <DiscussionDeleteModal
      title={`Delete ${discusstionType}`}
      body={`Are you sure you want to delete your ${discusstionType}? This can't be undone.`}
      deleteButtonText={`Delete ${discusstionType}`}
      cancelCallback={hideModal}
      successCallback={onSuccessDelete}
      errorCallback={onErrorDelete}
      deleteUrl={deleteUrl}
    />
  ));

  function onSuccessDelete() {
    toast.success(
      `${
        discusstionType[0].toUpperCase() + discusstionType.slice(1)
      } has been deleted!`,
    );

    hideModal();

    // redirect to aggregate page
    // the toast message will kept on the page since it's a client-side redirect
    if (
      isIndexablePage(asPath.split('?')[0]) &&
      discusstionType !== CommentV2Type.Reply
    ) {
      push(`/products/${deal.slug}/${discusstionType}s`);
    } else {
      mutateComment();
      mutateReviewStats();
    }
  }

  function onErrorDelete() {
    toast.error('Something went wrong, please try again.');
    hideModal();
  }

  function onDelete(callback: Function) {
    showModal();
    callback();
  }

  return (
    <div className="relative">
      <Menu
        as="div"
        className="relative"
        onClick={() => setShowOptions(!showOptions)}
      >
        {({ open, close }) => (
          <>
            <Menu.Button
              ref={buttonRef}
              className="text-slate hover:text-gray-500"
              data-testid="discussion-options"
            >
              <span className="sr-only">More options</span>
              <FontAwesomeIcon icon={faEllipsis} width="16" height="16" />
            </Menu.Button>
            <Transition
              show={open}
              unmount={false}
              enter="md:transition md:ease-out md:duration-100"
              enterFrom="md:transform md:opacity-0 md:scale-95"
              enterTo="md:transform md:opacity-100 md:scale-100"
              leave="md:transition md:ease-in md:duration-75"
              leaveFrom="md:transform md:opacity-100 md:scale-100"
              leaveTo="md:transform md:opacity-0 md:scale-95"
            >
              <Menu.Items
                static
                className="absolute right-0 space-y-2 rounded border border-gravel bg-white py-2 px-4 shadow"
              >
                <Menu.Item>
                  <button
                    type="button"
                    className="text-bolt hover:text-bolt-20"
                    onClick={() => onDelete(close)}
                  >
                    Delete
                  </button>
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
}

export default DiscussionOptions;
